import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Code, Layers, Users2 } from 'lucide-react';
import { portfolioData } from '../data/mock';

const Skills = () => {
  const { skills } = portfolioData;
  const [activeTab, setActiveTab] = useState('technical');

  return (
    <section id="skills" className="py-20 bg-slate-900 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute top-0 left-10 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-72 h-72 bg-blue-600/5 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4">
            Technical <span className="text-blue-400">Skills</span>
          </h2>
          <div className="w-20 h-1 bg-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Comprehensive expertise across automation, leadership, and modern engineering practices
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-12">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 bg-slate-800/50 border border-slate-700 mb-8">
              <TabsTrigger
                value="technical"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white flex items-center gap-2"
              >
                <Code size={18} />
                <span className="hidden sm:inline">Technical</span>
              </TabsTrigger>
              <TabsTrigger
                value="leadership"
                className="data-[state=active]:bg-blue-600 data-[state=active]:text-white flex items-center gap-2"
              >
                <Users2 size={18} />
                <span className="hidden sm:inline">Leadership</span>
              </TabsTrigger>
            </TabsList>

            {/* Technical Skills Tab */}
            <TabsContent value="technical" className="space-y-8">
              {/* Skill Bars */}
              <div className="grid sm:grid-cols-2 gap-6 mb-8">
                {skills.technical.slice(0, 8).map((skill, index) => (
                  <div key={index} className="group">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium text-sm sm:text-base">{skill.name}</span>
                      <span className="text-blue-400 text-sm font-semibold">{skill.level}%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out group-hover:shadow-lg group-hover:shadow-blue-500/50"
                        style={{
                          width: `${skill.level}%`,
                          animation: 'slideIn 1s ease-out'
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Technology Categories */}
              <div className="space-y-6">
                <h3 className="text-2xl font-semibold text-white flex items-center gap-3">
                  <Layers className="text-blue-400" size={28} />
                  Technology Stack
                </h3>
                <div className="grid gap-6">
                  {Object.entries(skills.categories).map(([category, techs]) => (
                    <Card
                      key={category}
                      className="bg-slate-800/50 border-slate-700 hover:border-blue-500/50 transition-all duration-300 p-6"
                    >
                      <h4 className="text-lg font-semibold text-blue-400 mb-4">{category}</h4>
                      <div className="flex flex-wrap gap-2">
                        {techs.map((tech, idx) => (
                          <Badge
                            key={idx}
                            variant="outline"
                            className="border-slate-600 bg-slate-900/50 text-gray-300 hover:border-blue-500 hover:text-blue-400 hover:bg-blue-500/10 transition-all duration-300 cursor-default"
                          >
                            {tech}
                          </Badge>
                        ))}
                      </div>
                    </Card>
                  ))}
                </div>
              </div>
            </TabsContent>

            {/* Leadership Skills Tab */}
            <TabsContent value="leadership" className="space-y-8">
              <div className="grid sm:grid-cols-2 gap-6">
                {skills.leadership.map((skill, index) => (
                  <div key={index} className="group">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-white font-medium text-sm sm:text-base">{skill.name}</span>
                      <span className="text-blue-400 text-sm font-semibold">{skill.level}%</span>
                    </div>
                    <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 rounded-full transition-all duration-1000 ease-out group-hover:shadow-lg group-hover:shadow-blue-500/50"
                        style={{
                          width: `${skill.level}%`,
                          animation: 'slideIn 1s ease-out'
                        }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Leadership Highlights */}
              <Card className="bg-slate-800/50 border-slate-700 p-6 mt-8">
                <h4 className="text-xl font-semibold text-white mb-4">Leadership Achievements</h4>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                    <p className="text-gray-300">Successfully managed and mentored teams of up to 10 engineers</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                    <p className="text-gray-300">Awarded "Best Manager of the Year" at BharatPe</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                    <p className="text-gray-300">Key decision maker in system design and architecture</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                    <p className="text-gray-300">Led cross-functional collaboration reducing escalations by 99%</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                    <p className="text-gray-300">Provided training on automation and system design best practices</p>
                  </div>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      <style jsx>{`
        @keyframes slideIn {
          from {
            width: 0;
          }
        }
      `}</style>
    </section>
  );
};

export default Skills;